import React, { useState } from 'react';
import axios from 'axios';

import { FEEDBACK_API, API_BASE } from '../config';
const API = FEEDBACK_API;
const BETA_API = `${API_BASE}/beta-signup/admin`;

function StarDisplay({ rating }) {
  if (!rating) return <span style={{ fontSize: '0.75rem', color: '#bbb' }}>—</span>;
  return (
    <span style={{ fontSize: '0.9rem', letterSpacing: '0.05em' }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} style={{ color: n <= rating ? '#f59e0b' : '#e5e7eb' }}>★</span>
      ))}
      <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: '0.3rem' }}>
        {['','Not useful','Slightly useful','Useful','Very useful','Excellent'][rating]}
      </span>
    </span>
  );
}

export default function AdminDashboard() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [tab, setTab] = useState('feedback');

  // Feedback tab state
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Beta-request tab state
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');

  // Allowlist tab state
  const [allowlist, setAllowlist] = useState([]);
  const [allowlistLoading, setAllowlistLoading] = useState(false);
  const [allowlistInput, setAllowlistInput] = useState('');
  const [allowlistNote, setAllowlistNote] = useState('');
  const [allowlistMessage, setAllowlistMessage] = useState('');

  // Users tab state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    await fetchFeedback(password, 'all', 1);
  };

  const fetchFeedback = async (adminPassword, ratingFilter = 'all', pageNum = 1) => {
    setLoading(true);
    try {
      const params = { password: adminPassword, page: pageNum };
      if (ratingFilter !== 'all') params.rating = ratingFilter;
      const response = await axios.get(API + '/admin', { params });
      if (response.data.success) {
        setAuthenticated(true);
        setFeedback(pageNum === 1 ? response.data.feedback : [...feedback, ...response.data.feedback]);
        setStats(response.data.stats);
        setHasMore(response.data.pagination.hasMore);
        setPage(pageNum);
      }
    } catch (error) {
      if (error.response?.status === 401) alert('Invalid admin password');
      else if (error.response?.status === 503) alert('Admin access not configured on this server. Set ADMIN_PASSWORD in your .env file.');
      else { console.error('Feedback fetch error:', error); alert('Failed to fetch feedback'); }
    }
    setLoading(false);
  };

  const changeFilter = async (newFilter) => {
    setFilter(newFilter);
    setPage(1);
    setFeedback([]);
    await fetchFeedback(password, newFilter, 1);
  };

  const loadMore = async () => {
    await fetchFeedback(password, filter, page + 1);
  };

  // ── Beta-request tab ────────────────────────────────────────────
  const fetchPending = async () => {
    setPendingLoading(true);
    setPendingMessage('');
    try {
      const res = await axios.post(`${BETA_API}/list-pending`, { password });
      if (res.data.ok) setPending(res.data.pending || []);
    } catch (error) {
      if (error.response?.status === 401) alert('Session expired — refresh and log in again.');
      else { console.error('Pending fetch error:', error); alert('Failed to load pending requests'); }
    }
    setPendingLoading(false);
  };

  const decide = async (id, action) => {
    const verb = action === 'approve' ? 'Approve' : 'Decline';
    if (!window.confirm(`${verb} this beta request?`)) return;
    setPendingMessage('');
    try {
      const res = await axios.post(`${BETA_API}/decide`, { password, id, action });
      if (res.data.ok) {
        setPendingMessage(action === 'approve' ? 'Approved. Applicant has been emailed.' : 'Declined. Applicant has been emailed.');
        await fetchPending();
        if (action === 'approve' && tab === 'beta') await fetchAllowlist();
      }
    } catch (error) {
      console.error('Decide error:', error);
      alert(error.response?.data?.error || 'Failed to record decision');
    }
  };

  // ── Allowlist tab ───────────────────────────────────────────────
  const fetchAllowlist = async () => {
    setAllowlistLoading(true);
    setAllowlistMessage('');
    try {
      const res = await axios.post(`${BETA_API}/allowlist/list`, { password });
      if (res.data.ok) setAllowlist(res.data.allowlist || []);
    } catch (error) {
      if (error.response?.status === 401) alert('Session expired — refresh and log in again.');
      else { console.error('Allowlist fetch error:', error); alert('Failed to load allowlist'); }
    }
    setAllowlistLoading(false);
  };

  const addAllowlist = async () => {
    const emails = allowlistInput
      .split(/[\s,;]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      setAllowlistMessage('Paste at least one email — one per line, or comma-separated.');
      return;
    }
    setAllowlistMessage('');
    try {
      const res = await axios.post(`${BETA_API}/allowlist/add`, {
        password,
        emails,
        note: allowlistNote.trim() || undefined
      });
      if (res.data.ok) {
        const { added, skipped, rejected } = res.data;
        const parts = [`Added ${added}`];
        if (skipped > 0) parts.push(`${skipped} already on list`);
        if (rejected && rejected.length > 0) parts.push(`${rejected.length} rejected (invalid format)`);
        setAllowlistMessage(parts.join(' · '));
        setAllowlistInput('');
        setAllowlistNote('');
        await fetchAllowlist();
      }
    } catch (error) {
      console.error('Allowlist add error:', error);
      setAllowlistMessage(error.response?.data?.error || 'Failed to add emails');
    }
  };

  const removeAllowlist = async (email) => {
    if (!window.confirm(`Remove ${email} from the allowlist?`)) return;
    try {
      const res = await axios.post(`${BETA_API}/allowlist/remove`, { password, email });
      if (res.data.ok) await fetchAllowlist();
    } catch (error) {
      console.error('Allowlist remove error:', error);
      alert(error.response?.data?.error || 'Failed to remove email');
    }
  };

  // ── Users tab ───────────────────────────────────────────────────
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await axios.post(`${BETA_API}/users`, { password });
      if (res.data.ok) setUsers(res.data.users || []);
    } catch (error) {
      if (error.response?.status === 401) alert('Session expired — refresh and log in again.');
      else { console.error('Users fetch error:', error); alert('Failed to load users'); }
    }
    setUsersLoading(false);
  };

  const switchTab = async (newTab) => {
    setTab(newTab);
    if (newTab === 'beta' && pending.length === 0) await fetchPending();
    if (newTab === 'allowlist' && allowlist.length === 0) await fetchAllowlist();
    if (newTab === 'users' && users.length === 0) await fetchUsers();
  };

  if (!authenticated) {
    return (
      <div style={{
        minHeight: '100vh', background: '#f7f7f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
      }}>
        <div style={{
          background: 'white', borderRadius: 16, padding: '2.5rem',
          width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 300, color: '#1a1a2e', marginBottom: '0.25rem' }}>
            Guru<strong style={{ color: '#1D9E75', fontWeight: 700 }}>One</strong>
          </div>
          <h2 style={{ margin: '0 0 1.75rem', fontSize: '1.1rem', color: '#1a1a2e', fontWeight: 700 }}>
            Admin Dashboard
          </h2>
          <form onSubmit={login}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                Admin password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{
                  width: '100%', padding: '0.75rem 0.9rem', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: '0.9rem',
                  fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !password}
              style={{
                width: '100%', padding: '0.85rem', borderRadius: 8,
                background: '#1D9E75', color: 'white', border: 'none',
                fontSize: '0.95rem', fontWeight: 600,
                cursor: loading || !password ? 'not-allowed' : 'pointer',
                opacity: loading || !password ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >
              {loading ? 'Logging in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const avgQuality = (() => {
    const rated = feedback.filter(f => f.quality_rating);
    if (!rated.length) return null;
    return (rated.reduce((s, f) => s + f.quality_rating, 0) / rated.length).toFixed(1);
  })();

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => switchTab(key)}
      style={{
        padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none',
        background: tab === key ? '#1a1a2e' : 'transparent',
        color: tab === key ? '#fff' : '#1a1a2e',
        fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f9', padding: '2rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 300, color: '#1a1a2e' }}>
            Guru<strong style={{ color: '#1D9E75', fontWeight: 700 }}>One</strong>
          </div>
          <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
            Admin Dashboard
          </h1>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'inline-flex', gap: '0.25rem',
          background: '#fff', padding: '0.35rem', borderRadius: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '1.75rem'
        }}>
          {tabBtn('feedback', 'Feedback')}
          {tabBtn('beta', 'Beta requests')}
          {tabBtn('users', 'Users')}
          {tabBtn('allowlist', 'Allowlist')}
        </div>

        {/* ── Feedback tab ─────────────────────────────────────── */}
        {tab === 'feedback' && (
          <>
            {/* Stats */}
            {stats && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem', marginBottom: '2rem',
              }}>
                {[
                  { value: stats.total, label: 'Total responses', color: '#1a1a2e' },
                  { value: `${stats.positive} / ${stats.total}`, label: '👍 Easy to use', color: '#1D9E75' },
                  { value: `${stats.negative} / ${stats.total}`, label: '👎 Not easy', color: '#dc2626' },
                  { value: stats.total > 0 ? `${Math.round((stats.positive / stats.total) * 100)}%` : '—', label: 'Ease rate', color: '#1D9E75' },
                  { value: avgQuality ? `${avgQuality} / 5` : '—', label: '★ Avg value rating', color: '#f59e0b' },
                ].map((s, i) => (
                  <div key={i} style={{ background: 'white', borderRadius: 12, padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.2rem' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* By guru breakdown */}
            {stats?.byGuru?.length > 0 && (
              <div style={{ background: 'white', borderRadius: 12, padding: '1.25rem', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.75rem' }}>By tool</div>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  {stats.byGuru.map(g => (
                    <div key={g.guru} style={{ fontSize: '0.78rem', color: '#555' }}>
                      <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{g.guru}</span>
                      {' — '}{g.total} responses · {Math.round((g.positive / g.total) * 100)}% easy
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem' }}>
              {['all', 'positive', 'negative'].map(f => (
                <button key={f} onClick={() => changeFilter(f)} disabled={loading}
                  style={{
                    padding: '0.45rem 1rem', borderRadius: 6, border: 'none',
                    background: filter === f ? '#1D9E75' : '#e5e7eb',
                    color: filter === f ? 'white' : '#333',
                    fontSize: '0.78rem', fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer', textTransform: 'capitalize',
                  }}
                >
                  {f === 'all' ? `All (${stats?.total || 0})` :
                   f === 'positive' ? `👍 Easy (${stats?.positive || 0})` :
                   `👎 Not easy (${stats?.negative || 0})`}
                </button>
              ))}
            </div>

            {/* Feedback list */}
            <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              {feedback.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                  No feedback found.
                </div>
              ) : (
                feedback.map((item, i) => (
                  <div key={item.id} style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: i < feedback.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '1rem' }}>{item.rating === 1 ? '👍' : '👎'}</span>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e' }}>
                        {item.guru} Guru
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#999' }}>
                        {item.email || 'Unknown'} · {new Date(item.timestamp).toLocaleString('en-GB')}
                      </span>
                    </div>

                    {item.quality_rating && (
                      <div style={{ marginBottom: '0.6rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#888', marginRight: '0.5rem' }}>Value:</span>
                        <StarDisplay rating={item.quality_rating} />
                      </div>
                    )}

                    {item.comment && (
                      <div style={{
                        background: '#fef9f0', border: '1px solid #fde68a',
                        borderRadius: 6, padding: '0.65rem 0.9rem',
                        fontSize: '0.82rem', color: '#78350f', marginBottom: '0.6rem',
                      }}>
                        <strong>What could be better: </strong>{item.comment}
                      </div>
                    )}

                    {item.response_received && (
                      <details style={{ fontSize: '0.75rem', color: '#888' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>View AI response</summary>
                        <div style={{
                          marginTop: '0.5rem', background: '#f8f9fa', border: '1px solid #e9ecef',
                          borderRadius: 6, padding: '0.65rem', whiteSpace: 'pre-wrap',
                          maxHeight: 200, overflow: 'auto',
                        }}>
                          {item.response_received.substring(0, 600)}{item.response_received.length > 600 ? '…' : ''}
                        </div>
                      </details>
                    )}
                  </div>
                ))
              )}

              {hasMore && (
                <div style={{ padding: '1rem', textAlign: 'center' }}>
                  <button onClick={loadMore} disabled={loading}
                    style={{
                      padding: '0.65rem 1.5rem', borderRadius: 8,
                      background: '#1D9E75', color: 'white', border: 'none',
                      fontSize: '0.82rem', fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                    }}
                  >
                    {loading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Beta requests tab ────────────────────────────────── */}
        {tab === 'beta' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                Pending beta requests
              </h2>
              <button onClick={fetchPending} disabled={pendingLoading}
                style={{
                  padding: '0.4rem 0.85rem', borderRadius: 6, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#555', fontSize: '0.78rem', fontWeight: 600,
                  cursor: pendingLoading ? 'not-allowed' : 'pointer'
                }}>
                {pendingLoading ? 'Loading…' : 'Refresh'}
              </button>
              {pendingMessage && (
                <span style={{ fontSize: '0.78rem', color: '#1D9E75', fontWeight: 600 }}>{pendingMessage}</span>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              {pending.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                  {pendingLoading ? 'Loading…' : 'No pending requests right now.'}
                </div>
              ) : (
                pending.map((item, i) => (
                  <div key={item.id} style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: i < pending.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>{item.name}</span>
                      <span style={{ fontSize: '0.78rem', color: '#555' }}>{item.email}</span>
                      <span style={{ fontSize: '0.72rem', color: '#999' }}>
                        · {new Date(item.created_at).toLocaleString('en-GB')}
                      </span>
                    </div>
                    {item.linkedin_url && (
                      <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem' }}>
                        <a href={item.linkedin_url} target="_blank" rel="noreferrer"
                          style={{ color: '#0898A0', textDecoration: 'none', fontWeight: 600 }}>
                          {item.linkedin_url} ↗
                        </a>
                      </div>
                    )}
                    <div style={{ fontSize: '0.78rem', color: '#5C5C58', marginBottom: '0.8rem' }}>
                      <span style={{ marginRight: '1rem' }}><strong>Size:</strong> {item.company_size}</span>
                      <span style={{ marginRight: '1rem' }}><strong>Revenue:</strong> {item.annual_revenue}</span>
                      <span><strong>Challenge:</strong> {item.key_challenge}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.6rem' }}>
                      <button onClick={() => decide(item.id, 'approve')}
                        style={{
                          padding: '0.5rem 1.1rem', borderRadius: 7, border: 'none',
                          background: '#1D9E75', color: '#fff',
                          fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                        }}>
                        Approve and let them in
                      </button>
                      <button onClick={() => decide(item.id, 'reject')}
                        style={{
                          padding: '0.5rem 1.1rem', borderRadius: 7, border: '1.5px solid #dc2626',
                          background: '#fff', color: '#dc2626',
                          fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                        }}>
                        Decline politely
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Users tab ────────────────────────────────────────── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                Registered users <span style={{ color: '#888', fontWeight: 400 }}>({users.length})</span>
              </h2>
              <button onClick={fetchUsers} disabled={usersLoading}
                style={{
                  padding: '0.4rem 0.85rem', borderRadius: 6, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#555', fontSize: '0.78rem', fontWeight: 600,
                  cursor: usersLoading ? 'not-allowed' : 'pointer'
                }}>
                {usersLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              {users.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                  {usersLoading ? 'Loading…' : 'No users yet.'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: '#f7f7f9', textAlign: 'left' }}>
                      <th style={{ padding: '0.7rem 1rem', color: '#1a1a2e', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name / email</th>
                      <th style={{ padding: '0.7rem 1rem', color: '#1a1a2e', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Team</th>
                      <th style={{ padding: '0.7rem 1rem', color: '#1a1a2e', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Revenue</th>
                      <th style={{ padding: '0.7rem 1rem', color: '#1a1a2e', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Top challenge</th>
                      <th style={{ padding: '0.7rem 1rem', color: '#1a1a2e', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                        <td style={{ padding: '0.8rem 1rem', verticalAlign: 'top' }}>
                          <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{u.business_name || '—'}</div>
                          <div style={{ fontSize: '0.74rem', color: '#888', marginTop: 2 }}>{u.email}</div>
                          {u.role_label && (
                            <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: 1 }}>{u.role_label}</div>
                          )}
                        </td>
                        <td style={{ padding: '0.8rem 1rem', verticalAlign: 'top', color: '#444' }}>
                          {u.team_size_band || <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.8rem 1rem', verticalAlign: 'top', color: '#444' }}>
                          {u.annual_revenue_band || <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.8rem 1rem', verticalAlign: 'top', color: '#444', maxWidth: 280 }}>
                          {u.current_top_challenge || <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.8rem 1rem', verticalAlign: 'top', color: '#888', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                          {new Date(u.created_at).toLocaleDateString('en-GB')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Allowlist tab ────────────────────────────────────── */}
        {tab === 'allowlist' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                Pre-approved emails
              </h2>
              <button onClick={fetchAllowlist} disabled={allowlistLoading}
                style={{
                  padding: '0.4rem 0.85rem', borderRadius: 6, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#555', fontSize: '0.78rem', fontWeight: 600,
                  cursor: allowlistLoading ? 'not-allowed' : 'pointer'
                }}>
                {allowlistLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: '1.25rem', maxWidth: 760 }}>
              Anyone on this list skips the website + work-email checks on the beta signup form. Approving a no-website request also auto-adds the applicant here.
            </p>

            <div style={{ background: 'white', borderRadius: 12, padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.55rem' }}>
                Add emails
              </div>
              <textarea
                value={allowlistInput}
                onChange={e => setAllowlistInput(e.target.value)}
                placeholder="Paste one or more email addresses — one per line, or comma-separated"
                rows={4}
                style={{
                  width: '100%', padding: '0.65rem 0.85rem', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: '0.85rem',
                  fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
                  resize: 'vertical'
                }}
              />
              <input
                type="text"
                value={allowlistNote}
                onChange={e => setAllowlistNote(e.target.value)}
                placeholder="Optional note (e.g. 'Founders' Society referral')"
                style={{
                  width: '100%', padding: '0.55rem 0.85rem', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: '0.82rem',
                  fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
                  marginTop: '0.6rem',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '0.8rem' }}>
                <button onClick={addAllowlist}
                  style={{
                    padding: '0.55rem 1.1rem', borderRadius: 7, border: 'none',
                    background: '#1D9E75', color: '#fff',
                    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                  }}>
                  Add to allowlist
                </button>
                {allowlistMessage && (
                  <span style={{ fontSize: '0.78rem', color: '#555' }}>{allowlistMessage}</span>
                )}
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              {allowlist.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                  {allowlistLoading ? 'Loading…' : 'Allowlist is empty.'}
                </div>
              ) : (
                allowlist.map((row, i) => (
                  <div key={row.email} style={{
                    padding: '0.85rem 1.5rem',
                    borderBottom: i < allowlist.length - 1 ? '1px solid #f0f0f0' : 'none',
                    display: 'flex', alignItems: 'center', gap: '1rem',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.88rem', color: '#1a1a2e', fontWeight: 600 }}>{row.email}</div>
                      <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.15rem' }}>
                        Added {new Date(row.added_at).toLocaleString('en-GB')}
                        {row.added_by ? ` · ${row.added_by}` : ''}
                        {row.note ? ` · ${row.note}` : ''}
                      </div>
                    </div>
                    <button onClick={() => removeAllowlist(row.email)}
                      style={{
                        padding: '0.35rem 0.85rem', borderRadius: 6, border: '1.5px solid #e5e7eb',
                        background: '#fff', color: '#dc2626',
                        fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                      }}>
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
